import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Heart, ExternalLink } from 'lucide-react';
import content from '../../data/content.json';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="about" className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {content.about.title}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {content.about.description}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div variants={itemVariants}>
              <img
                src="/images/teaching-2.jpg"
                alt="Focused learning environment"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Personalized Approach
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    One-on-one attention tailored to each student's unique learning style and pace, ensuring no one gets left behind.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Experienced Faculty
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Our tutors have 5+ years of teaching experience and are trained to mentor students with patience, care, and understanding.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Heart className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Our Mission
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {content.about.mission}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Meet Our Founder
                </h3>
                <h4 className="text-xl text-purple-600 dark:text-purple-400 font-semibold mb-4">
                  {content.founder.name}
                </h4>
                <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                  {content.founder.bio}
                </p>
                <div className="space-y-2">
                  {content.founder.credentials.map((credential, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-purple-600 dark:bg-purple-400 rounded-full"></div>
                      <span className="text-gray-700 dark:text-gray-300">{credential}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <a
                    href="https://www.urbanpro.com/hyderabad/boda-s-experienced-tutor-and-certified-engineer-from-jntu-university"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-semibold transition-colors duration-200"
                  >
                    <span>View Verified Profile on UrbanPro</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div>
                <img
                  src="/images/founder-sireesha.jpg"
                  alt="Boda Sireesha - Founder"
                  className="rounded-2xl shadow-lg max-w-[300px] w-full mx-auto h-auto object-cover"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
